// AI assistant tools: the admin playground, knowledge-base suggestions (the
// questions visitors asked that it couldn't answer), and the agent's drafted
// reply. Thin typed wrappers over apiFetch.

import type { CompanyProfile, MessageCitation, ReadMoreLink, RichBlock } from '@prakash-software/shared';
import { apiFetch, isApiError } from './api';
import type { KnowledgeEntry } from './knowledge-api';

export interface PlaygroundTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface PlaygroundAnswer {
  ok: true;
  text: string;
  richText: RichBlock[];
  citations: MessageCitation[];
  readMore: ReadMoreLink[];
  /** Knowledge chunks found for the last question; null = knowledge base off. */
  matched: number | null;
  /** Would be logged as a suggestion in a real conversation. */
  gap: 'no-match' | 'not-sure' | null;
}

/** Answer a test conversation as a visitor would be answered. `draft` tries
 *  unsaved instructions / company profile edits. */
export async function playgroundReply(
  siteId: string,
  messages: PlaygroundTurn[],
  draft?: { systemPrompt: string | null; companyProfile: CompanyProfile | null },
): Promise<PlaygroundAnswer> {
  return apiFetch<PlaygroundAnswer>(`/api/admin/sites/${siteId}/playground`, {
    method: 'POST',
    body: JSON.stringify({ messages, ...(draft ? { draft } : {}) }),
  });
}

export type SuggestionStatus = 'open' | 'answered' | 'dismissed';

export interface KbSuggestion {
  id: string;
  siteId: string;
  question: string;
  reason: 'no-match' | 'not-sure';
  count: number;
  lastSessionId: string | null;
  status: SuggestionStatus;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
}

export async function listSuggestions(
  siteId: string,
  status: SuggestionStatus,
): Promise<{ suggestions: KbSuggestion[]; open: number }> {
  return apiFetch(`/api/admin/sites/${siteId}/knowledge/suggestions?status=${status}`);
}

export async function setSuggestionStatus(siteId: string, id: string, status: SuggestionStatus): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/knowledge/suggestions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function deleteSuggestion(siteId: string, id: string): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/knowledge/suggestions/${id}`, { method: 'DELETE' });
}

/** Add the answer as a Q&A entry and mark the suggestion answered. */
export async function answerSuggestion(
  siteId: string,
  id: string,
  input: { question: string; answer: string },
): Promise<KnowledgeEntry> {
  const { entry } = await apiFetch<{ entry: KnowledgeEntry }>(
    `/api/admin/sites/${siteId}/knowledge/suggestions/${id}/answer`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return entry;
}

/** A draft of the agent's next reply in a conversation. */
export async function suggestReply(conversationId: string): Promise<string> {
  const { text } = await apiFetch<{ text: string }>(`/api/conversations/${conversationId}/suggest-reply`, {
    method: 'POST',
  });
  return text;
}

/** A human reason for a failed assistant call. */
export function assistantErrorMessage(err: unknown): string {
  if (!isApiError(err)) return 'The request failed. Check your connection and try again.';
  const body = (err.body ?? {}) as { error?: string; message?: string };
  switch (body.error) {
    case 'no-provider':
      return 'No AI provider is configured. A platform admin sets one in Admin → System → AI.';
    case 'rate-limited':
      return 'Too many requests in a minute. Wait a moment and try again.';
    case 'empty-reply':
      return 'The model returned an empty answer. Try again, or pick a non-reasoning model in Admin → System → AI.';
    case 'empty-transcript':
      return 'The visitor has not written anything yet.';
    case 'provider-error':
      return `The AI provider returned an error${body.message ? `: ${body.message}` : '.'}`;
    default:
      return body.message ?? `The request failed (${err.status}).`;
  }
}
